import { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Snackbar, Typography, Tabs, Tab } from "@mui/material";
import AllArticles from "../articles/all/AllArticles";
import AllTips from "../tips/all/AllTips";
import FloatingButton from "../floating-button/FloatingButton";
import classes from "./HomePageLayout.module.scss";
import { socket } from "../../../socket";
import { NotificationSeverities } from "../../enums/NotificationSeverities";
import { Content } from "../../enums/Content";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";

const CLICKAWAY_EVENT = 'clickaway';

const HomePageLayout = () => {
  const [isNotificationOpen, setIsNotificationOpen] = useState<boolean>(false);
  const tipFilters = useSelector(
    (state: RootState) => state.tipFilters.tipFilters
  );

  const hasTipsFilters = useMemo(() => {
    if (!tipFilters || typeof tipFilters !== 'object') {
      return false;
    }

    return Boolean(
      tipFilters.hasWords ||
      (tipFilters.tags?.length > 0) ||
      tipFilters.authors?.length > 0 ||
      tipFilters.date
    );
  }, [tipFilters]);

  const [contentType, setContentType] = useState<string>(hasTipsFilters ? Content.TIPS : Content.ARTICLES);

  useEffect(() => {
    const onNewContent = () => {
      setIsNotificationOpen(true);
    };

    socket.on('new-content', onNewContent);

    return () => {
      socket.off('new-content', onNewContent);
      socket.disconnect();
    };
  }, []);

  const notificationOnCloseHandler = (event?: SyntheticEvent | Event, reason?: string) => {
    if (CLICKAWAY_EVENT === reason) {
      return;
    }

    setIsNotificationOpen(false);
  };

  const handleContentTypeChange = (event: SyntheticEvent, newContentType: string) => {
    setContentType(newContentType);
  };

  return (
    <>
      <Box className='border-bottom mb-4'>
        <Tabs
          value={contentType}
          onChange={handleContentTypeChange}
          aria-label="Content type selection"
          //MUI overriding styles, waste too much time on this
          sx={{
            '& .MuiTab-root': {
              minWidth: 120,
              fontSize: '1rem',
              fontWeight: 'bold',
              padding: '12px 24px',
              color: '#00A767;',
              opacity: 0.5,
              '&.Mui-selected': {
                color: '#00A767;',
                opacity: 1,
              },
            },
            '& .MuiTabs-indicator': {
              height: 3,
              backgroundColor: '#00A767;',
            }
          }}
        >
          <Tab label={Content.ARTICLES} value={Content.ARTICLES} />
          <Tab label={Content.TIPS} value={Content.TIPS} />
        </Tabs>
      </Box>
      {contentType === Content.ARTICLES ? <AllArticles /> : <AllTips />}
      <FloatingButton />
      <Snackbar open={isNotificationOpen} onClose={notificationOnCloseHandler} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert className='p-2' variant='filled' onClose={() => setIsNotificationOpen(false)} severity={NotificationSeverities.INFO}>
          <Typography className='d-flex align-items-center justify-content-center flex-wrap'>
            There is a new content. Please
            {<Button className={classes['alert-button']} variant='text' onClick={() => window.location.reload()}>
              Reload
            </Button>}
            the page.
          </Typography>
        </Alert>
      </Snackbar>
    </>
  );
};

export default HomePageLayout;
